@if (loading) {
  <div class="rounded-2xl border border-gray-200 bg-white p-8 text-center dark:border-gray-800 dark:bg-white/[0.03]">
    <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Loading Event Planner</h3>
    <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
      Pulling the latest planner template and answers for this event.
    </p>
  </div>
} @else if (loadError) {
  <div class="rounded-2xl border border-red-200 bg-red-50 p-8 text-center dark:border-red-900/50 dark:bg-red-950/20">
    <h3 class="text-lg font-semibold text-red-700 dark:text-red-300">Planner unavailable</h3>
    <p class="mt-2 text-sm text-red-600 dark:text-red-200">{{ loadError }}</p>
    <div class="mt-4 flex justify-center">
      <app-button
        [label]="'Retry'"
        [style]="'secondary'"
        (click)="loadPlanner()">
      </app-button>
    </div>
  </div>
} @else if (planner) {
  <div class="space-y-5">
    @for (section of plannerSections; track trackSection($index, section); let sectionIndex = $index) {
      <section
        class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <div class="border-b border-gray-200 pb-3 dark:border-gray-800">
          <h4 class="text-base font-semibold uppercase tracking-[0.08em] text-brand-700 dark:text-brand-300">{{ section.title }}</h4>
        </div>
        <div class="pt-4">
          @if (section.intro_text) {
            <div class="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500 dark:border-gray-800 dark:bg-gray-900/40 dark:text-gray-400">
              {{ section.intro_text }}
            </div>
          }
        </div>

          @if (section.section_type === 'contacts') {
            @if (preContactEntries(sectionIndex).length) {
              <div class="space-y-0 pt-2">
                @for (item of preContactEntries(sectionIndex); track trackEntry(item.index, item.entry)) {
                  <div class="grid gap-3 border-b border-gray-100 py-3 last:border-b-0 lg:grid-cols-[15rem_minmax(0,1fr)] dark:border-gray-800">
                    <div class="text-sm font-medium text-gray-700 dark:text-gray-300">
                      <div class="flex items-center gap-2 pt-3">
                        <span>{{ item.entry.label }}</span>
                      </div>
                    </div>
                    <div>
                      @if (isTextEntry(item.entry)) {
                        <app-text-input
                          [control]="entryAnswerTextControl(sectionIndex, item.index)"
                          [placeholder]="getPlaceholder(item.entry)">
                        </app-text-input>
                      }

                      @if (isTextareaEntry(item.entry)) {
                        <app-textarea
                          [control]="entryAnswerTextControl(sectionIndex, item.index)"
                          [rows]="4">
                        </app-textarea>
                      }

                      @if (isSelectEntry(item.entry)) {
                        <app-select
                          [control]="entryAnswerTextControl(sectionIndex, item.index)"
                          [choices]="getSelectChoices(item.entry)"
                          [placeholder]="'— Select —'">
                        </app-select>
                      }

                      @if (getHint(item.entry)) {
                        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">{{ getHint(item.entry) }}</p>
                      }
                    </div>
                  </div>
                }
              </div>
            }

            <div class="space-y-3 pt-2">
              @for (item of contactEntries(sectionIndex); track trackEntry(item.index, item.entry)) {
                <div class="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                  <div class="flex flex-col gap-4">
                    <div class="flex items-start justify-between gap-3">
                      <div>
                        <p class="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400">{{ item.entry.label }}</p>
                      </div>
                      @if (canRemoveEntry(item.entry)) {
                        <button
                          type="button"
                          class="text-sm font-medium text-red-600 transition hover:text-red-700 dark:text-red-300 dark:hover:text-red-200"
                          (click)="removeEntry(sectionIndex, item.index)">
                          Remove
                        </button>
                      }
                    </div>

                    <div class="grid gap-4 lg:grid-cols-[10rem_minmax(0,1fr)] lg:gap-y-3">
                      <div class="text-sm font-medium text-gray-700 dark:text-gray-300 lg:pt-3">
                        Role
                      </div>
                      <div>
                        <app-select
                          [control]="contactRoleControl(sectionIndex, item.index)"
                          [choices]="getContactRoleChoices(item.entry)"
                          [placeholder]="'— Select —'">
                        </app-select>
                      </div>

                      <div class="text-sm font-medium text-gray-700 dark:text-gray-300 lg:pt-3">
                        {{ getContactDetailsLabel(item.entry) }}
                      </div>
                      <div>
                        <app-text-input
                          [control]="contactDetailsControl(sectionIndex, item.index)"
                          [placeholder]="getContactDetailsPlaceholder(item.entry)">
                        </app-text-input>
                      </div>
                    </div>
                  </div>
                </div>
              }
            </div>

            <div class="mt-4">
              <button
                type="button"
                class="inline-flex items-center gap-1.5 rounded-md border border-dashed border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700 transition hover:bg-red-100 dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-300 dark:hover:bg-red-950/30"
                (click)="addContact(sectionIndex)">
                <lucide-icon [name]="PlusIcon" [size]="14"></lucide-icon>
                Add another contact
              </button>
            </div>

            <div class="mt-4 border-t border-dashed border-gray-200 pt-4 dark:border-gray-700">
              @if (contactsCustomEntries(sectionIndex).length) {
                <div class="space-y-0">
                  @for (item of contactsCustomEntries(sectionIndex); track trackEntry(item.index, item.entry)) {
                    <div class="grid gap-3 border-b border-gray-100 py-3 last:border-b-0 lg:grid-cols-[15rem_minmax(0,1fr)] dark:border-gray-800">
                      <div class="text-sm font-medium text-gray-700 dark:text-gray-300">
                        <div class="flex items-center gap-2 pt-3">
                          @if (item.entry.is_custom) {
                            <lucide-icon
                              [name]="item.entry.is_note ? FileTextIcon : CircleHelpIcon"
                              [size]="14"
                              [ngClass]="item.entry.is_note ? 'text-amber-600 dark:text-amber-300' : 'text-blue-600 dark:text-blue-300'">
                            </lucide-icon>
                          }
                          <span>{{ item.entry.label }}</span>
                        </div>
                      </div>
                      <div>
                        @if (isTextEntry(item.entry)) {
                          <app-text-input
                            [control]="entryAnswerTextControl(sectionIndex, item.index)"
                            [placeholder]="getPlaceholder(item.entry)">
                          </app-text-input>
                        }

                        @if (isTextareaEntry(item.entry)) {
                          <app-textarea
                            [control]="entryAnswerTextControl(sectionIndex, item.index)"
                            [rows]="4">
                          </app-textarea>
                        }

                        @if (isSelectEntry(item.entry)) {
                          <app-select
                            [control]="entryAnswerTextControl(sectionIndex, item.index)"
                            [choices]="getSelectChoices(item.entry)"
                            [placeholder]="'— Select —'">
                          </app-select>
                        }

                        @if (getHint(item.entry)) {
                          <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">{{ getHint(item.entry) }}</p>
                        }

                        @if (canRemoveEntry(item.entry)) {
                          <div class="mt-2 flex justify-end">
                            <button
                              type="button"
                              class="text-sm font-medium text-red-600 transition hover:text-red-700 dark:text-red-300 dark:hover:text-red-200"
                              (click)="removeEntry(sectionIndex, item.index)">
                              Remove
                            </button>
                          </div>
                        }
                      </div>
                    </div>
                  }
                </div>
              }

              <div class="mt-4 flex flex-wrap gap-3">
                <button
                  type="button"
                  class="inline-flex items-center gap-1.5 rounded-md border border-dashed border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600 transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 dark:border-gray-700 dark:bg-transparent dark:text-gray-300 dark:hover:border-blue-800 dark:hover:bg-blue-950/20 dark:hover:text-blue-300"
                  (click)="addCustomQuestion(sectionIndex)">
                  <lucide-icon [name]="CircleHelpIcon" [size]="14"></lucide-icon>
                  Add question
                </button>
                <button
                  type="button"
                  class="inline-flex items-center gap-1.5 rounded-md border border-dashed border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600 transition hover:border-amber-300 hover:bg-amber-50 hover:text-amber-700 dark:border-gray-700 dark:bg-transparent dark:text-gray-300 dark:hover:border-amber-800 dark:hover:bg-amber-950/20 dark:hover:text-amber-300"
                  (click)="addCustomNote(sectionIndex)">
                  <lucide-icon [name]="FileTextIcon" [size]="14"></lucide-icon>
                  Add note
                </button>
              </div>

              @if (contactsNotesEntry(sectionIndex); as notesItem) {
                <div class="mt-4 grid gap-3 border-t border-gray-100 py-3 lg:grid-cols-[15rem_minmax(0,1fr)] dark:border-gray-800">
                  <div class="text-sm font-medium text-gray-700 dark:text-gray-300">
                    <div class="flex items-center gap-2 pt-3">
                      <span>{{ notesItem.entry.label }}</span>
                    </div>
                  </div>
                  <div>
                    <app-textarea
                      [control]="entryAnswerTextControl(sectionIndex, notesItem.index)"
                      [rows]="4">
                    </app-textarea>
                  </div>
                </div>
              }
            </div>
          } @else {
            <div class="space-y-0 pt-2">
              @for (entry of section.entries; track trackEntry($index, entry); let entryIndex = $index) {
                <div class="grid gap-3 border-b border-gray-100 py-3 last:border-b-0 lg:grid-cols-[15rem_minmax(0,1fr)] dark:border-gray-800">
                  <div class="text-sm font-medium text-gray-700 dark:text-gray-300">
                    <div class="flex items-center gap-2 pt-3">
                      @if (entry.is_custom) {
                        <lucide-icon
                          [name]="entry.is_note ? FileTextIcon : CircleHelpIcon"
                          [size]="14"
                          [ngClass]="entry.is_note ? 'text-amber-600 dark:text-amber-300' : 'text-blue-600 dark:text-blue-300'">
                        </lucide-icon>
                      }
                      <span>{{ entry.label }}</span>
                    </div>
                  </div>
                  <div>
                    @if (isTextEntry(entry)) {
                      <app-text-input
                        [control]="entryAnswerTextControl(sectionIndex, entryIndex)"
                        [placeholder]="getPlaceholder(entry)">
                      </app-text-input>
                    }

                    @if (isTextareaEntry(entry)) {
                      <app-textarea
                        [control]="entryAnswerTextControl(sectionIndex, entryIndex)"
                        [rows]="4">
                      </app-textarea>
                    }

                    @if (isSelectEntry(entry)) {
                      <app-select
                        [control]="entryAnswerTextControl(sectionIndex, entryIndex)"
                        [choices]="getSelectChoices(entry)"
                        [placeholder]="'— Select —'">
                      </app-select>
                    }

                    @if (getHint(entry)) {
                      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">{{ getHint(entry) }}</p>
                    }

                    @if (canRemoveEntry(entry)) {
                      <div class="mt-2 flex justify-end">
                        <button
                          type="button"
                          class="text-sm font-medium text-red-600 transition hover:text-red-700 dark:text-red-300 dark:hover:text-red-200"
                          (click)="removeEntry(sectionIndex, entryIndex)">
                          Remove
                        </button>
                      </div>
                    }
                  </div>
                </div>
              }
            </div>

            <div class="mt-4 flex flex-wrap gap-3 border-t border-dashed border-gray-200 pt-4 dark:border-gray-700">
              <button
                type="button"
                class="inline-flex items-center gap-1.5 rounded-md border border-dashed border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600 transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 dark:border-gray-700 dark:bg-transparent dark:text-gray-300 dark:hover:border-blue-800 dark:hover:bg-blue-950/20 dark:hover:text-blue-300"
                (click)="addCustomQuestion(sectionIndex)">
                <lucide-icon [name]="CircleHelpIcon" [size]="14"></lucide-icon>
                Add question
              </button>
              <button
                type="button"
                class="inline-flex items-center gap-1.5 rounded-md border border-dashed border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600 transition hover:border-amber-300 hover:bg-amber-50 hover:text-amber-700 dark:border-gray-700 dark:bg-transparent dark:text-gray-300 dark:hover:border-amber-800 dark:hover:bg-amber-950/20 dark:hover:text-amber-300"
                (click)="addCustomNote(sectionIndex)">
                <lucide-icon [name]="FileTextIcon" [size]="14"></lucide-icon>
                Add note
              </button>
            </div>
          }
      </section>
    }

    <div class="flex justify-end border-t border-gray-200 pt-5 dark:border-gray-800">
      <app-button
        [label]="saving ? 'Saving…' : 'Save Planner'"
        [disabled]="saving || plannerForm.pristine"
        (click)="savePlanner()">
      </app-button>
    </div>
  </div>

  <app-modal
    [title]="addEntryModalTitle"
    [show]="isAddEntryModalOpen"
    [closeButtonLabel]="'Cancel'"
    [confirmButtonLabel]="addEntryConfirmLabel"
    [confirmDisabled]="!addEntryForm.controls.label.value?.trim()"
    [compact]="true"
    (onClose)="closeAddEntryModal()"
    (onConfirm)="confirmAddEntry()">
    <div class="space-y-8">
      <app-text-input
        [control]="addEntryForm.controls.label"
        [label]="addEntryPrimaryFieldLabel"
        [placeholder]="addEntryPrimaryFieldPlaceholder">
      </app-text-input>

      @if (addEntryKind === 'note') {
        <div class="pt-4">
          <app-textarea
            [control]="addEntryForm.controls.answer_text"
            [label]="addEntryValueFieldLabel"
            [rows]="4">
          </app-textarea>
        </div>
      } @else {
        <div class="pt-4">
          <app-text-input
            [control]="addEntryForm.controls.answer_text"
            [label]="addEntryValueFieldLabel">
          </app-text-input>
        </div>
      }
    </div>
  </app-modal>
}
